import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { IaCSecurityScanner } from "./IaCSecurityScanner";
import { UploadScanTab } from "./UploadScanTab";
import { 
  Code2, ChevronLeft, Loader2, Download, Copy, Check, Award,
  FolderTree, FileCode, RefreshCw, GitBranch, AlertTriangle, Shield, CheckCircle2, Lock, FileText, Scan, Upload
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iacTools = [
  { id: "terraform", name: "Terraform", ext: "tf", desc: "HashiCorp IaC for multi-cloud" },
  { id: "cloudformation", name: "CloudFormation", ext: "yaml", desc: "AWS native IaC" },
  { id: "bicep", name: "Bicep", ext: "bicep", desc: "Azure native IaC" },
  { id: "cdk", name: "AWS CDK", ext: "ts", desc: "TypeScript-based IaC" },
  { id: "pulumi", name: "Pulumi", ext: "py", desc: "Programming language IaC" },
  { id: "ansible", name: "Ansible", ext: "yaml", desc: "Configuration management" }
];

const getLanguage = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  const langMap = {
    'tf': 'hcl', 'yaml': 'yaml', 'yml': 'yaml', 'json': 'json',
    'py': 'python', 'ts': 'typescript', 'js': 'javascript',
    'sh': 'bash', 'md': 'markdown', 'bicep': 'bicep'
  };
  return langMap[ext] || 'plaintext';
};

export const Phase4Export = ({ project, onUpdateProject, onBack }) => {
  const { user, token, hasAccess } = useAuth();
  const [selectedTool, setSelectedTool] = useState("");
  const [iacData, setIacData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (project?.phase4?.iac_tool) {
      setSelectedTool(project.phase4.iac_tool);
      setIacData({
        repository_structure: project.phase4.repository_structure,
        files: project.phase4.code_files
      });
      const files = project.phase4.code_files;
      if (files && Object.keys(files).length > 0) {
        setSelectedFile(Object.keys(files)[0]);
      }
    }
  }, [project?.id]);

  const generateIaC = async (tool) => {
    // Check subscription
    if (!hasAccess('growth')) {
      toast.error("IaC export requires Growth or Enterprise subscription");
      return;
    }
    
    try {
      setGenerating(true);
      setSelectedTool(tool);
      setError(null);
      const response = await axios.post(`${API}/ai/iac`, {
        project_id: project.id,
        iac_tool: tool
      }, { 
        timeout: 180000,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setIacData({
        repository_structure: response.data.repository_structure,
        files: response.data.code_files
      });
      const files = response.data.code_files;
      if (files && Object.keys(files).length > 0) {
        setSelectedFile(Object.keys(files)[0]);
      }
      onUpdateProject({
        ...project,
        phase4: response.data,
        current_phase: 4
      });
      toast.success("Certified IaC code generated!");
    } catch (err) {
      let errorMsg = "Failed to generate IaC. Please try again.";
      if (err.code === 'ECONNABORTED') {
        errorMsg = "Request timed out. Please try again.";
      } else if (err.response?.status === 403) {
        errorMsg = "IaC export requires Growth or Enterprise subscription";
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (content) => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard!");
  };

  const downloadFile = (filename, content) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadAll = () => {
    if (!iacData?.files) return;
    Object.entries(iacData.files).forEach(([filename, content]) => {
      downloadFile(filename, content);
    });
    toast.success("All files downloaded!");
  };

  const downloadProductionBundle = async () => {
    if (!hasAccess('growth')) {
      toast.error("IaC export requires Growth or Enterprise subscription");
      return;
    }
    if (!selectedTool) {
      toast.error("Generate IaC first");
      return;
    }
    try {
      toast.loading("Packaging production bundle...", { id: "bundle" });
      const response = await axios.get(
        `${API}/ai/iac/bundle/${project.id}/${selectedTool}`,
        {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(project.name || 'architecture').replace(/[^a-z0-9-_]/gi, '-').slice(0, 50)}-${selectedTool}-bundle.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Production bundle downloaded — ready to deploy!", { id: "bundle" });
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to build bundle";
      toast.error(detail, { id: "bundle" });
    }
  };

  const downloadCertificate = async () => {
    try {
      const response = await axios.get(`${API}/export/${project.id}`);
      const blob = new Blob([response.data], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name || 'architecture'}-certificate.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded!");
    } catch (error) {
      toast.error("Failed to download certificate");
    }
  };

  const downloadCertificatePDF = async () => {
    if (!hasAccess('growth')) {
      toast.error("PDF certificate requires Growth or Enterprise subscription");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/export/${project.id}/pdf`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name || 'architecture'}_certificate.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF Certificate downloaded!");
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("PDF certificate requires Growth or Enterprise subscription");
      } else {
        toast.error("Failed to download PDF certificate");
      }
    }
  };

  const auditResults = project?.audit_results || {};
  const canExportIaC = hasAccess('growth');

  return (
    <div className="animate-fade-in">
      <div className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-[#00ff9d]/10 border border-[#00ff9d]/30 flex items-center justify-center">
            <Award className="w-5 h-5 text-[#00ff9d]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Phase 4: Certified Export</h1>
            <p className="text-sm md:text-base text-zinc-500">Download audit-certified IaC code</p>
          </div>
        </div>
        
        {iacData && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={downloadProductionBundle}
              className="bg-[#00ff9d] text-black hover:bg-[#00ff9d]/90 font-semibold"
              data-testid="download-production-bundle-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Production Bundle (.zip)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadAll}
              className="border-zinc-700 text-zinc-300 hover:bg-white/5"
              data-testid="download-all-files-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Files Only
            </Button>
            <Button
              size="sm"
              onClick={downloadCertificate}
              className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-white/5"
              variant="outline"
            >
              <FileText className="w-4 h-4 mr-2" />
              Markdown
            </Button>
            <Button
              size="sm"
              onClick={downloadCertificatePDF}
              disabled={!canExportIaC}
              className={`${canExportIaC ? 'bg-[#00ff9d] text-black hover:bg-[#00ff9d]/90' : 'bg-zinc-800 text-zinc-500'}`}
              data-testid="download-pdf-btn"
            >
              {!canExportIaC && <Lock className="w-4 h-4 mr-2" />}
              <Award className="w-4 h-4 mr-2" />
              PDF Certificate
            </Button>
          </div>
        )}
      </div>

      {/* Subscription Banner for Free Users */}
      {!canExportIaC && (
        <Card className="mb-6 border-yellow-500/30 bg-yellow-500/5">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Lock className="w-8 h-8 text-yellow-500" />
              <div className="flex-1">
                <h3 className="text-white font-semibold">Upgrade to Export</h3>
                <p className="text-sm text-zinc-400">IaC export and PDF certificates require Growth or Enterprise subscription</p>
              </div>
              <Button
                size="sm"
                className="bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
                onClick={() => window.location.href = '/'}
              >
                View Plans
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Certification Badge */}
      {iacData && (
        <Card className="mb-6 border-[#00ff9d]/30 bg-[#00ff9d]/5">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <div className="w-16 h-16 rounded-full bg-[#00ff9d]/10 border-2 border-[#00ff9d]/50 flex items-center justify-center">
                <Award className="w-8 h-8 text-[#00ff9d]" />
              </div>
              <div className="text-center md:text-left flex-1">
                <h3 className="text-white font-bold text-lg">Deployment Readiness Certificate</h3>
                <p className="text-sm text-zinc-400 mb-2">This architecture has passed all audit requirements</p>
                <div className="flex flex-wrap justify-center md:justify-start gap-3">
                  <div className="flex items-center gap-1 text-xs text-zinc-400">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    FinOps: {auditResults.finops?.budget_status || 'PASS'}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-zinc-400">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Resilience: {auditResults.resilience_score || 85}/100
                  </div>
                  <div className="flex items-center gap-1 text-xs text-zinc-400">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Compliance: Passed
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tool Selection */}
      {!iacData && !generating && (
        <div className="space-y-6">
          <Card className="bg-[#09090b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-[#00f0ff]" />
                Select IaC Tool
                {!canExportIaC && (
                  <span className="ml-2 px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Growth Plan
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {iacTools.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => generateIaC(tool.id)}
                    disabled={!canExportIaC}
                    className={`text-left p-4 bg-black/30 border border-white/5 rounded-sm transition-all group ${
                      canExportIaC 
                        ? 'hover:border-[#00f0ff]/30 hover:bg-[#00f0ff]/5 cursor-pointer' 
                        : 'opacity-60 cursor-not-allowed'
                    }`}
                    data-testid={`select-${tool.id}-btn`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-8 h-8 rounded-sm bg-zinc-800 flex items-center justify-center ${
                        canExportIaC ? 'group-hover:bg-[#00f0ff]/10' : ''
                      }`}>
                        <Code2 className={`w-4 h-4 text-zinc-400 ${canExportIaC ? 'group-hover:text-[#00f0ff]' : ''}`} />
                      </div>
                      <span className="font-semibold text-white">{tool.name}</span>
                    </div>
                    <p className="text-sm text-zinc-500">{tool.desc}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Generating State */}
      {generating && (
        <Card className="bg-[#09090b] border-white/5">
          <CardContent className="py-20 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-[#00ff9d]/10 border border-[#00ff9d]/30 flex items-center justify-center mb-6">
              <Loader2 className="w-8 h-8 text-[#00ff9d] animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Generating Certified {selectedTool} Code</h3>
            <p className="text-zinc-500 text-center max-w-md">
              Creating audit-compliant infrastructure code...
            </p>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && !generating && (
        <Card className="bg-[#09090b] border-red-500/20">
          <CardContent className="py-16 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-sm bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Generation Failed</h3>
            <p className="text-zinc-500 text-center max-w-md mb-6">{error}</p>
            <Button
              onClick={() => generateIaC(selectedTool)}
              className="bg-[#00ff9d] text-black hover:bg-[#00ff9d]/90 rounded-sm"
            >
              <Code2 className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Generated Code */}
      {iacData && !generating && !error && (
        <Tabs defaultValue="code" className="w-full">
          <TabsList className="bg-zinc-900/50 border border-zinc-800 mb-4">
            <TabsTrigger value="code" className="data-[state=active]:bg-[#00f0ff]/10 data-[state=active]:text-[#00f0ff]">
              <FileCode className="w-4 h-4 mr-2" />
              Generated Code
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-purple-600/20 data-[state=active]:text-purple-400">
              <Scan className="w-4 h-4 mr-2" />
              Security Scan
            </TabsTrigger>
            <TabsTrigger value="upload" className="data-[state=active]:bg-emerald-600/20 data-[state=active]:text-emerald-400">
              <Upload className="w-4 h-4 mr-2" />
              Upload & Scan
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="code">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6">
              {/* File Tree */}
              <Card className="bg-[#09090b] border-white/5 lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FolderTree className="w-4 h-4 text-[#00f0ff]" />
                      Repository
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => generateIaC(selectedTool)}
                      className="h-6 px-2 text-zinc-500 hover:text-white"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px] md:h-[400px]">
                    <div className="space-y-1">
                      {iacData.files && Object.keys(iacData.files).map((filename) => (
                        <button
                          key={filename}
                          onClick={() => setSelectedFile(filename)}
                          className={`w-full text-left px-3 py-2 rounded-sm text-xs md:text-sm font-mono flex items-center gap-2 transition-colors ${
                            selectedFile === filename 
                              ? 'bg-[#00f0ff]/10 text-[#00f0ff] border border-[#00f0ff]/30' 
                              : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <FileCode className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{filename}</span>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Code Viewer */}
              <Card className="bg-[#09090b] border-white/5 lg:col-span-3">
                <CardHeader className="pb-3 border-b border-white/5">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-sm flex items-center gap-2">
                      <FileCode className="w-4 h-4 text-[#00f0ff]" />
                      {selectedFile || "Select a file"}
                    </CardTitle>
                    {selectedFile && iacData.files?.[selectedFile] && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(iacData.files[selectedFile])}
                          className="h-8 px-3 text-zinc-400 hover:text-white"
                        >
                          {copied ? <Check className="w-4 h-4 text-[#00ff9d]" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadFile(selectedFile, iacData.files[selectedFile])}
                          className="h-8 px-3 text-zinc-400 hover:text-white"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[300px] md:h-[400px]">
                    {selectedFile && iacData.files?.[selectedFile] ? (
                      <div className="code-block-wrapper">
                        <SyntaxHighlighter
                          language={getLanguage(selectedFile)}
                          style={atomDark}
                          customStyle={{
                            margin: 0,
                            padding: '1rem',
                            background: '#0a0a0b',
                            fontSize: '12px',
                            lineHeight: '1.6'
                          }}
                          showLineNumbers
                          lineNumberStyle={{ color: '#3f3f46', minWidth: '3em' }}
                        >
                          {iacData.files[selectedFile]}
                        </SyntaxHighlighter>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-zinc-500">
                        Select a file to view its contents
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="security">
            <IaCSecurityScanner 
              project={project} 
              onScanComplete={(results) => {
                if (onUpdateProject) {
                  onUpdateProject({ ...project, iac_scan_results: results });
                }
              }}
            />
          </TabsContent>
          
          <TabsContent value="upload">
            <UploadScanTab />
          </TabsContent>
        </Tabs>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-zinc-700 text-zinc-300 hover:bg-white/5 rounded-sm"
          data-testid="phase4-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Review
        </Button>

        {iacData && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#00ff9d] hidden md:inline">
              ✓ Architecture Certified
            </span>
            <Button
              onClick={downloadCertificate}
              className="bg-[#00ff9d] text-black font-semibold hover:bg-[#00ff9d]/90 rounded-sm px-6"
            >
              <Award className="w-4 h-4 mr-2" />
              Download Certificate
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
